import { useTranslation } from "react-i18next";
import TranslationEdit from "../translation-edit/TranslationEdit";
import { useEffect, useState } from "react";
import { api } from "../../../services/api/api";
import Switch from "../../switch/Switch";
import ServiceSelect from "../service/ServiceSelect";
import InvoicingMethodSelect from "../invoicing-method/InvoicingMethodSelect";

/**
 * @param data - Data to edit
 * @param onChange - On change event, trigger on field change
 * @param onSubmit - On submit event, trigger enter key down on field
 * @param submitted - is form submitted
 */
export default function WorkorderTypeForm({
  data,
  onChange,
  onSubmit,
  submitted
}: {
  data?: EasyTech.WorkorderType,
  onChange: (name: string, value: any) => void,
  onSubmit: () => void,
  submitted?: boolean
}) {
  const { t } = useTranslation();
  const [languages, setLanguagesState] = useState<EasyTech.Language[]>([]);

  const [selectedService1, setSelectedService1] = useState<EasyTech.Service | null>(null);
  const [selectedService2, setSelectedService2] = useState<EasyTech.Service | null>(null);

  /**
   * useEffect: get languages list
   */
  useEffect(() => {
    api.get("users/languages")
      .then(ret => {
        setLanguagesState(ret as EasyTech.Language[]);
      })
      .catch((err) => {
        console.error(err);
      });
  }, []);

  /**
   * Resolve service IDs from the services collection (or fallback serviceId)
   */
  const s1Id = data?.services?.[0]?.serviceId || data?.serviceId;
  const s2Id = data?.services?.[1]?.serviceId;

  /**
   * useEffect: load Service 1 object
   */
  useEffect(() => {
    if (s1Id) {
      api.get(`users/services/${s1Id}`)
        .then(ret => setSelectedService1(ret as EasyTech.Service))
        .catch(() => setSelectedService1(null));
    } else {
      setSelectedService1(null);
    }
  }, [s1Id]);

  /**
   * useEffect: load Service 2 object
   */
  useEffect(() => {
    if (s2Id) {
      api.get(`users/services/${s2Id}`)
        .then(ret => setSelectedService2(ret as EasyTech.Service))
        .catch(() => setSelectedService2(null));
    } else {
      setSelectedService2(null);
    }
  }, [s2Id]);

  /**
   * Helper to update a service entry in the services collection
   */
  const updateServiceAtIndex = (
    index: number,
    updates: Partial<EasyTech.WorkorderTypeService>
  ) => {
    const currentServices: EasyTech.WorkorderTypeService[] = data?.services
      ? [...data.services]
      : [];

    while (currentServices.length <= index) {
      currentServices.push({ serviceId: null, invoicingMethod: null });
    }

    currentServices[index] = {
      ...currentServices[index],
      ...updates,
    };

    onChange("services", currentServices);
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="field">
        <label htmlFor="label">{t("Label")}</label>
        <input type="text"
          value={data?.label || ""}
          onChange={(e) => onChange("label", e.target.value)}
          className={`${(submitted && (!data || !data?.label)) && "error"}`}
          placeholder={t("Label")} />
      </div>

      <div className="field">
        <label htmlFor="translation">{t("Translation")}</label>
        <TranslationEdit key={data?.id} data={data?.translations} onChange={(v) => onChange("translations", v)} languages={languages} />
      </div>

      {/* Odoo service 1 */}
      <div className="field">
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {t("admin:odooService1", "Odoo service 1")}
        </label>
        <div className="grid grid-cols-2 gap-3 items-center">
          <ServiceSelect<EasyTech.Service>
            data={selectedService1}
            isMulti={false}
            isClearable={true}
            placeholder={t("admin:selectService", "Select a service")}
            onChange={(value) => {
              const service = value as EasyTech.Service | null;
              setSelectedService1(service);
              updateServiceAtIndex(0, {
                serviceId: service?.id ?? null,
                serviceLabel: service?.label ?? null,
              });
            }}
          />
          <InvoicingMethodSelect
            value={data?.services?.[0]?.invoicingMethod}
            placeholder={t("admin:invoicingMethod", "Invoicing method")}
            onChange={(val) => updateServiceAtIndex(0, { invoicingMethod: val })}
          />
        </div>
      </div>

      {/* Odoo service 2 */}
      <div className="field">
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {t("admin:odooService2", "Odoo service 2")}
        </label>
        <div className="grid grid-cols-2 gap-3 items-center">
          <ServiceSelect<EasyTech.Service>
            data={selectedService2}
            isMulti={false}
            isClearable={true}
            placeholder={t("admin:selectService", "Select a service")}
            onChange={(value) => {
              const service = value as EasyTech.Service | null;
              setSelectedService2(service);
              updateServiceAtIndex(1, {
                serviceId: service?.id ?? null,
                serviceLabel: service?.label ?? null,
              });
            }}
          />
          <InvoicingMethodSelect
            value={data?.services?.[1]?.invoicingMethod}
            placeholder={t("admin:invoicingMethod", "Invoicing method")}
            onChange={(val) => updateServiceAtIndex(1, { invoicingMethod: val })}
          />
        </div>
      </div>

      {/* Enable / Switch */}
      <div className="field">
        <label className="block text-sm font-medium text-gray-700 mb-1">{t("Enable")}</label>
        <Switch
          checked={data?.enabled}
          onChange={(e) => onChange("enabled", e)}
        />
      </div>
    </div>
  );
}